import React from 'react';
import PropTypes from 'prop-types';
import Bars3Icon from '@heroicons/react/24/solid/Bars3Icon';
import { useTranslation } from "react-i18next";
import { useContext, useState, useEffect } from 'react';
import { AuthContext } from "../context/AuthContext";
import SvgIcon from '@mui/material/SvgIcon';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import Badge from '@mui/material/Badge';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import { usePopover } from '../hooks/usePopover';
import { AccountPopover } from './popovers/AccountPopover';
import { NotificationPopover } from './popovers/NotificationPopover';
import { RequestPopover } from './popovers/RequestPopover';

const SIDE_NAV_WIDTH = 280;
const TOP_NAV_HEIGHT = 56;

const actionButtonSx = {
  bgcolor: 'primary.alpha12',
  cursor: 'pointer',
  height: 40,
  width: 40,
  transition: 'all 0.2s ease-in-out',
  boxShadow: 'none',
  '&:hover': {
    bgcolor: 'primary.alpha30',
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    bgcolor: 'action.disabledBackground',
  },
};

export const TopNav = React.memo((props) => {
  const { onNavOpen, language, setLanguage, projects, users, loading } = props;
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const accountPopover = usePopover();
  const notificationPopover = usePopover();
  const requestPopover = usePopover();
  const { user, adminMode } = useContext(AuthContext);

  const [notificationCount, setNotificationCount] = useState(0);
  const [requestCount, setRequestCount] = useState(0);

  useEffect(() => {
    //if (!projects || projects.length === 0) return; // Early exit if projects is empty or not loaded

    if (adminMode) {
      //console.log("adminMode on");
      if (!users || users.length === 0)return; 

      const count = users.reduce((acc, user) => {

        //console.log(user);
        const userData = user.projects || [];

        userData.forEach((project, dataIndex) => {

          const projectData = project.data || [];
          //console.log(`Project has ${projectData.length} data items`);

          projectData.forEach((dataItem, dataIndex) => {
            const processingData = dataItem.processing_requests || [];
            //console.log(`  Data item ${dataIndex} has ${processingData.length} processing_requests`);

            processingData.forEach((processingItem, resultIndex) => {
              //console.log(`    Result item ${resultIndex} viewed status: ${resultItem.attributes.viewed}`);
              if (processingItem.done === false) { // Only count if viewed is explicitly `false`
                acc++;
              }
            });
          });
        });
        return acc;
      }, 0);

      if (requestCount !== count) {
        setRequestCount(count); // Only update if count has changed
      }
    } else {
      //console.log("adminMode off");
      if (!projects || projects.length === 0) return; 

      const count = projects.reduce((acc, project) => {
        const projectData = project.attributes.data.data || [];
        //console.log(`Project has ${projectData.length} data items`);

        projectData.forEach((dataItem, dataIndex) => {
          const resultsData = dataItem.attributes.results.data || [];
          //console.log(`  Data item ${dataIndex} has ${resultsData.length} results`);

          resultsData.forEach((resultItem, resultIndex) => {
            //console.log(`    Result item ${resultIndex} viewed status: ${resultItem.attributes.viewed}`);
            if (resultItem.attributes.viewed === false) { // Only count if viewed is explicitly `false`
              acc++;
            }
          });
        });
        return acc;
      }, 0);

      if (notificationCount !== count) {
        setNotificationCount(count); // Only update if count has changed
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects,users]);

  const handleNotificationUpdate = (count) => {
    setNotificationCount(count);
  };

  const { t } = useTranslation();

  function stringAvatar(user) {
    if (user) {
      if (user.name.split(' ').length > 1) {
        return {
          children: `${user.name.split(' ')[0][0]}${user.name.split(' ')[1][0]}`.toUpperCase(),
        };
      }

      if (user.name.split(' ').length === 1) {
        return {
          children: `${user.name.split(' ')[0][0]}`.toUpperCase(),
        };
      } else {
        return ''
      }
    }
  }

  return (
    <>
      <Box
        component="header"
        sx={{
          backgroundColor: 'rgba(240, 242, 245, 0.8)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          left: {
            lg: `${SIDE_NAV_WIDTH}px`
          },
          top: 0,
          width: {
            lg: `calc(100% - ${SIDE_NAV_WIDTH}px)`
          },
          zIndex: (theme) => theme.zIndex.appBar
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{
            minHeight: TOP_NAV_HEIGHT,
            px: 2,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            sx={{ alignItems: 'center' }}
          >
            {!lgUp && (
              <IconButton onClick={onNavOpen}>
                <SvgIcon fontSize="small">
                  <Bars3Icon />
                </SvgIcon>
              </IconButton>
            )}
          </Stack>
          {loading &&
            <Box sx={{ px: 2 }}>
              <Stack
                sx={{
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
                direction="row"
                spacing={2}
              >
                <CircularProgress size="2rem" />
                <Typography color="text.secondary" variant="h6">
                  {t("FetchingData")}
                </Typography>
              </Stack>
            </Box>
          }
          <Stack
            direction="row"
            spacing={2}
            sx={{ alignItems: 'center' }}
          >
            {!adminMode ?
              <IconButton
                sx={actionButtonSx}
                disabled={notificationCount === 0}
                onClick={notificationPopover.handleOpen}
                ref={notificationPopover.anchorRef}
              >
                <Badge badgeContent={notificationCount} color="error">
                  <SvgIcon>
                    <NotificationsNoneOutlinedIcon color="primary" />
                  </SvgIcon>
                </Badge>
              </IconButton>
            :
              <IconButton
                sx={actionButtonSx}
                disabled={requestCount === 0}
                onClick={requestPopover.handleOpen}
                ref={requestPopover.anchorRef}
              >
                <Badge badgeContent={requestCount} color="warning">
                  <SvgIcon>
                    <QueryStatsIcon color="primary" />
                  </SvgIcon>
                </Badge>
              </IconButton>
            }
            <Avatar
              {...stringAvatar(user)}
              onClick={accountPopover.handleOpen}
              ref={accountPopover.anchorRef}
              sx={{
                bgcolor: 'primary.alpha20',
                color: 'primary.main',
                cursor: 'pointer',
                height: 40,
                width: 40,
                '&:hover': {
                  bgcolor: 'primary.alpha50',
                },
              }}
            />
          </Stack>
        </Stack>
      </Box>

      <AccountPopover
        anchorEl={accountPopover.anchorRef.current}
        open={accountPopover.open}
        onClose={accountPopover.handleClose}
        language={language}
        setLanguage={setLanguage}
      />
      <NotificationPopover
        anchorEl={notificationPopover.anchorRef.current}
        open={notificationPopover.open}
        onClose={notificationPopover.handleClose}
        projects={projects}
        onUpdateNotificationCount={handleNotificationUpdate}
      />
        <RequestPopover
        anchorEl={requestPopover.anchorRef.current}
        open={requestPopover.open}
        onClose={requestPopover.handleClose}
        users={users}
      />
    </>

  );
});

TopNav.propTypes = {
  onNavOpen: PropTypes.func
};
